//#region Imports

import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ButtonComponent } from '../../../shared/components/button/button.component';
import { ButtonRadiusEnum } from '../../../shared/components/button/types/button-radius.enum';
import { ButtonTypeEnum } from '../../../shared/components/button/types/button-type.enum';
import { IconComponent } from '../../../shared/components/icon/icon.component';
import { ModuleCardComponent } from '../../../shared/components/module-card/module-card.component';
import { CourseLocation } from '../../../shared/models/enums/course-location.enum';
import { Course } from '../../../shared/models/interfaces/course.interface';
import { Module } from '../../../shared/models/interfaces/module.interface';
import { Class } from '../../../shared/models/interfaces/module-class.interface';
import { COURSE_LOCATION_ICONS, COURSE_LOCATION_LABELS } from '../../../shared/models/records/course-location.record';
import { ProgressBannerComponent } from '../../../shared/components/progress-banner/progress-banner.component';
import { ModalStatusService } from '../../../shared/services/modal-status/modal-status.service';
import { CourseService } from '../../../shared/services/course/course.service';
import { MeService } from '../../../shared/services/me/me.service';
import { ModuleService } from '../../../shared/services/module/module.service';
import { ToastService } from '../../../shared/components/toast/service/toast.service';
import { UserCourseService } from '../../../shared/services/user-course/user-course.service';
import { SpinnerComponent } from '../../../shared/components/spinner/spinner.component';
import { getCrudErrors } from '../../../shared/utils/functions';

//#endregion

@Component({
  selector: 'l-course-details',
  standalone: true,
  imports: [CommonModule, RouterLink, ButtonComponent, IconComponent, ModuleCardComponent, ProgressBannerComponent, SpinnerComponent],
  templateUrl: './details.component.html',
})
export class CourseDetailsComponent implements OnInit {

  //#region Constructor

  constructor(
    private readonly activatedRoute: ActivatedRoute,
    private readonly router: Router,
    private readonly courseService: CourseService,
    private readonly moduleService: ModuleService,
    private readonly meService: MeService,
    private readonly userCourseService: UserCourseService,
    private readonly modalStatusService: ModalStatusService,
    private readonly toastService: ToastService,
  ) {}

  //#endregion

  //#region Public Properties

  public course: Course | null = null;

  public isLoading: boolean = true;

  public notFound: boolean = false;

  public userId: string = '';

  public userCourseProgress: number = 0;

  public hasUserCourseEnrollment: boolean = false;

  public buttonTypeEnum: typeof ButtonTypeEnum = ButtonTypeEnum;

  public buttonRadiusEnum: typeof ButtonRadiusEnum = ButtonRadiusEnum;

  //#endregion

  //#region Public Methods

  public async ngOnInit(): Promise<void> {
    await this.initializeDetails();
  }

  public get coverImageUrl(): string {
    return this.course?.coverImageUrl ?? 'assets/images/course-default.png';
  }

  public get locationLabel(): string {
    return COURSE_LOCATION_LABELS[this.course?.location ?? CourseLocation.ONLINE];
  }

  public get locationIcon(): string {
    return COURSE_LOCATION_ICONS[this.course?.location ?? CourseLocation.ONLINE];
  }

  public get durationLabel(): string {
    const hours = (this.course?.durationMinutes ?? 0) / 60;
    const formatted = Number.isInteger(hours) ? hours.toString() : hours.toFixed(1).replace(/\.0$/, '');
    return `${formatted}h`;
  }

  public get courseModules(): Module[] {
    return this.course?.modules ?? [];
  }

  public get isEnrolled(): boolean {
    if (!this.course) return false;

    return this.hasUserCourseEnrollment;
  }

  public get buttonLabel(): string {
    if (!this.isEnrolled) return 'Obter curso';

    const progress = this.userCourseProgress;
    if (progress >= 100) return 'Recomeçar';
    if (progress > 0) return 'Continue aprendendo';
    return 'Comece a aprender!';
  }

  public async onButtonClick(): Promise<void> {
    if (!this.course) return;

    if (!this.isEnrolled) {
      await this.addUserToCourse();
      return;
    }

    this.navigateToCourseContents();
  }

  //#endregion

  //#region Private Methods

  private async initializeDetails(): Promise<void> {
    this.userId = this.meService.getMe()?.id ?? '';

    await this.loadCourse();
    await this.loadUserCourseProgress();
  }

  private async loadCourse(): Promise<void> {
    try {
      const courseId = this.activatedRoute.snapshot.paramMap.get('courseId');

      if (!courseId) {
        this.notFound = true;
        return;
      }

      const course = await this.courseService.getById(courseId);

      if (!course) {
        this.course = null;
        this.notFound = true;
        return;
      }

      let modules: Module[] = [];

      try {
        const baseModules = await this.moduleService.getByCourseId(course.id, { join: ['classes'] });
        const moduleIds = baseModules.map((module) => module.id);
        const classes = moduleIds.length
          ? await this.moduleService.getClassesByModuleIds(moduleIds)
          : [];

        const classesByModuleId = classes.reduce<Map<string, Class[]>>((accumulator, moduleClass) => {
          const moduleClasses = accumulator.get(moduleClass.moduleId) ?? [];
          moduleClasses.push(moduleClass);
          accumulator.set(moduleClass.moduleId, moduleClasses);
          return accumulator;
        }, new Map<string, Class[]>());

        modules = baseModules.map((module) => ({
          ...module,
          classes: [...(classesByModuleId.get(module.id) ?? module.classes ?? [])].sort((first, second) => first.order - second.order),
        }));
      } catch (error) {
        this.toastService.error(getCrudErrors(error)[0], 'Oops...');
      }

      this.course = {
        ...course,
        modules,
        modulesIds: modules.map((module) => module.id),
      };
      this.notFound = !course;
    } catch (error) {
      this.toastService.error(getCrudErrors(error)[0], 'Oops...');
      this.course = null;
      this.notFound = true;
    } finally {
      this.isLoading = false;
    }
  }

  private async loadUserCourseProgress(): Promise<void> {
    if (!this.course?.id) {
      this.userCourseProgress = 0;
      this.hasUserCourseEnrollment = false;
      return;
    }

    const userCourse = await this.userCourseService.getUserCourse(this.userId, this.course.id);
    this.hasUserCourseEnrollment = !!userCourse;
    this.userCourseProgress = userCourse?.completedAt
      ? 100
      : userCourse?.progress ?? 0;
  }

  private async addUserToCourse(): Promise<void> {
    if (!this.userId || !this.course?.id) return;

    if (this.hasUserCourseEnrollment) return;

    await this.userCourseService.enrollUserInCourse(this.userId, this.course.id);
    this.hasUserCourseEnrollment = true;
    this.userCourseProgress = 0;

    this.modalStatusService.open({
      title: 'Curso obtido com sucesso!',
      message: 'O curso já está disponível em Meus cursos, clique em \'Comece a aprender\' e inicie essa jornada de conhecimento!',
      actionLabel: 'Voltar',
    });

  }

  private navigateToCourseContents(): void {
    if (!this.course?.id) return;

    this.router.navigate(['/courses', this.course.id, 'content']).then();
  }

  //#endregion

}